import findKey from 'lodash/findKey';
import isEmpty from 'lodash/isEmpty';
import { getState } from './state';
import { buildSpectateStaticAssetUrl } from '~spa/Utils/Spectate';
import { getCurrentTheme } from '~shared/utils/user';
import {
    AWAY_SUFFIX,
    EXCLUDE_SCOREBOARD_MATCH_STATUS,
    RACING_SLUGS,
} from './constants';

export const TEAM_LOGO_PREFIX = '/upload/team-logos/';
export const TEAM_LOGO_LIGHT_MODE_PREFIX = '/upload/team-logos-light-mode/';

export const TOURNAMENT_LOGO_PREFIX = '/upload/tournament-logos/';
export const TOURNAMENT_SPORT_LOGO_PREFIX = '/upload/tournament-sport-logos/';

const THEME_LIGHT_MODE = 2;

let sports = {};

const getSports = () => {
    if (!isEmpty(sports)) {
        return sports;
    }

    sports = getState('sports');

    return sports;
};

/**
 * Check if the given slug is valid
 * @param {String} slug The slug to check
 * @returns {Boolean} Whether the slug is valid or not
 */
const isValidSportSlug = (slug) => typeof getSports()[slug] !== 'undefined';

/**
 * Returns sport name for a given slug
 * @param {String} slug Sport slug
 * @return {String} Sport name
 */
export function getSportNameBySlug(slug) {
    return isValidSportSlug(slug) ? getSports()[slug].name : null;
}

/**
 * Returns sport id for a given slug
 * @param {String} slug Sport slug
 * @return {Number} Sport id
 */
export function getSportIdBySlug(slug) {
    return isValidSportSlug(slug) ? parseInt(getSports()[slug].id, 10) : null;
}

/**
 * Return the sport slug for a given id
 * @param {Number} id The sport id
 * @return {String} The sport slug
 */
export function getSportSlugById(id) {
    return findKey(getSports(), { id: String(id) });
}

/**
 * Decides wether one selection has boosted-odds
 *
 * @param {Object} selectionsMap [selectionId] => selectionDetails
 * @return {Boolean} has at least one boosted-odd selection
 */
export const getHasBoostedOddsSelections = (selectionsMap = {}) => {
    return Object.values(selectionsMap)
        .some(selection => selection && selection.boosted_odds);
};

/**
 * get the team logo url
 * @param {String} sportSlug sport slug
 * @param {String} teamSlug team slug
 * @param {String} showAwayJersey show away jersey
 * @param {Integer | String} themeMode theme mode code
 * @param {String} suffix url suffix
 * @return {String} team logo url
 */
export const getTeamLogoUrl = (sportSlug, teamSlug, showAwayJersey = false, themeMode = getCurrentTheme(), suffix = 'png') => {

    const themeLightMode = parseInt(themeMode) === THEME_LIGHT_MODE;
    const themeModePrefix = themeLightMode ? TEAM_LOGO_LIGHT_MODE_PREFIX : TEAM_LOGO_PREFIX;
    const modifiedTeamSlug = `${teamSlug}${showAwayJersey ? AWAY_SUFFIX : ''}`;

    return buildSpectateStaticAssetUrl(`${themeModePrefix}${sportSlug}/${modifiedTeamSlug}.${suffix}`);
};

/**
 * get the tournament logo url
 * @param {String} sportSlug sport slug
 * @param {String} categorySlug category slug
 * @param {String} tournamentSlug tournament slug
 * @param {String} suffix url suffix
 * @return {String} tournament logo url
 */
export const getTournamentLogoUrl = (sportSlug, categorySlug, tournamentSlug, suffix = 'svg') => {
    return buildSpectateStaticAssetUrl(`${TOURNAMENT_LOGO_PREFIX}${sportSlug}/${categorySlug}/${tournamentSlug}.${suffix}`);
};

/**
 * get the sport logo url
 * @param {String} sportSlug sport slug
 * @param {String} categorySlug category slug
 * @param {String} tournamentSlug tournament slug
 * @param {String} suffix url suffix
 * @return {String} sport logo url
 */
export const getTournamentSportLogoUrl = (sportSlug, categorySlug, tournamentSlug, suffix = 'svg') => {
    return buildSpectateStaticAssetUrl(`${TOURNAMENT_SPORT_LOGO_PREFIX}${sportSlug}/${categorySlug}/${tournamentSlug}.${suffix}`);
};

export const isMatchStatusScoreUpdates = (status) => status && !EXCLUDE_SCOREBOARD_MATCH_STATUS.includes(status.toUpperCase());

export const isRacingSport = (sportSlug) => RACING_SLUGS.includes(sportSlug);
